import React from 'react'

import { Box, Divider, Grid, Stack, useTheme } from '@mui/material'

import { Skeleton, Tabs, TabPanel } from '@repo/ui'
import { Header, InfoBlock, InfoItem, InfoRow } from 'components'
import { ROUTES } from 'routes'

/**
 * Loading placeholder for the certificate detail page (§19).
 *
 * ## What it replaces
 *
 * `{isLoading ? <Loader /> : …}` — a centred spinner in place of a 122px header, a 53px tab row and
 * a 596px panel. Nothing was reserved, so the page materialised whole.
 *
 * ## Measured, not guessed
 *
 * Off `/certificates/:id` (viewport 1711, content column x=280 w=1407), Product tab:
 *
 *   wayfinding row   y=28  h=33   (starts at 28, not 24 — the Header carries `sx={{ pt: 1 }}`)
 *   title row        y=73  h=49
 *   title            300x31 at y=82, status chip 75x32 beside it
 *   header actions   View 90 / Archive 108, h=40, group w=335 (Activate omitted — see below)
 *   header total     y=24  h=122
 *   tab row          y=146 h=53, tabs Product 88 / Provenance 115 / Scans 72 / Activity 87
 *   panel            y=199 h=596, a `spacing={6}` Grid at x=256 w=1431
 *   left  md=5       gallery 572x572 square at x=280 y=223
 *   right md=7       x=876 w=811: InfoBlock 271 → gap 16 → Description accordion 116
 *                    → gap 16 → Details accordion 58   = 477
 *
 * ## The tab row is the REAL Tabs component
 *
 * That is possible because the tab routes are built from the URL's `:id`, which is known BEFORE the
 * fetch — so the labels, widths, indicator position, and the 53px row are not approximations, they
 * are the same component the loaded page renders. This is why the skeleton takes `certificateId`.
 * Real chrome that does not depend on the fetch beats a bar standing in for it.
 *
 * ## The gallery is a bare square, not `Slideshow`'s own skeleton
 *
 * `Slideshow images={null}` draws the square PLUS a five-thumbnail strip. That strip is right for a
 * multi-image product and ~126px too tall for a single-image one, and the image count is exactly
 * what is unknown before the fetch. Here that over-reserve WOULD shift the page: the square is the
 * tallest thing in the row (572 against the right column's 477), so it — not the info panel — sets
 * the 596px panel height. A bare square is the common denominator, same call as the product page.
 *
 * ## Where else it deliberately under-reserves
 *
 *   - **Activate** (113px) renders only for a certificate whose status is `inactive` or `created`.
 *   - The **Description accordion** is `defaultExpanded`, and its open height is its description's:
 *     116px for the one-line description measured here. One line is reserved.
 *   - The two **alert banners** (suspicious activity, tag tampering) are not reserved at all; they
 *     are exceptional states, and reserving one would put a ~90px hole above every healthy
 *     certificate.
 *
 * None of those three can shift the page below the panel, because the panel's height is set by the
 * square in the left column (572 + 24 padding = 596) and the right column stays well under it.
 */
export const CertificatePageSkeleton = ({ certificateId }: { certificateId: string }) => {
  const theme = useTheme()

  /**
   * A header action button, at the height `headerActionGroupSx` in Header.tsx enforces (40 —
   * `actionSize` default `medium`). A literal only because Header does not export it; it does not
   * affect footprint, since the title row carries its own `minHeight: 49`.
   */
  const ActionSkeleton = ({ width }: { width: number }) => (
    <Skeleton variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
  )

  /**
   * One accordion panel, built from the same tokens the real `Accordion` uses rather than pinned to
   * a measured height: a 56px (64 when expanded) summary row with the label inset past the chevron,
   * optionally followed by the `px: 3 / pb: 6 / pl: 10.5` details area. So the height falls out of
   * the padding, and only "one line of body copy" is an assumption.
   */
  const AccordionSkeleton = ({ expanded = false }: { expanded?: boolean }) => (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
      }}
    >
      <Stack sx={{ minHeight: expanded ? 64 : 56, px: 3, pl: 10.5, justifyContent: 'center' }}>
        <Skeleton width={96} height={18} />
      </Stack>
      {expanded && (
        <Box sx={{ px: 3, pb: 6, pt: 0, pl: 10.5 }}>
          <Skeleton height={26} sx={{ maxWidth: 520 }} />
        </Box>
      )}
    </Box>
  )

  return (
    <>
      <Header
        // Same `pt: 1` as the loaded header — without it every region below sits 4px high.
        sx={{ pt: 1 }}
        breadcrumbs={true}
        // A bar, NOT omitted: `Header` renders the trail only when this is truthy, and omitting it
        // also trips the branch that pulls the whole header up 24.5px.
        breadcrumbsOveride={<Skeleton width={150} height={16} />}
        // 300px is the measured width of a representative product title.
        title={<Skeleton width={300} height={31} />}
        // `Header`'s own pill placeholder for the status chip.
        status={null as any}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            {/* Activate is omitted — it renders only for an inactive/created certificate. */}
            <ActionSkeleton width={90} />
            <ActionSkeleton width={108} />
          </Stack>
        }
      />

      <Box>
        {/* The REAL tab row — the routes come from the URL's id, not the fetch. */}
        <Tabs
          tabs={[
            { label: 'Product', value: ROUTES.CERTIFICATE(certificateId) },
            { label: 'Provenance', value: ROUTES.CERTIFICATE_PROVENANCE(certificateId) },
            { label: 'Scans', value: ROUTES.CERTIFICATE_SCANS(certificateId) },
            { label: 'Activity', value: ROUTES.CERTIFICATE_ACTIVITY(certificateId) },
          ]}
        >
          <TabPanel sx={{ p: 0, pt: 6 }} value={ROUTES.CERTIFICATE(certificateId)}>
            {/* Same `spacing={6}` / 5-7 split as the Product tab. */}
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                {/*
                  The aspect ratio lives on a WRAPPER with the Skeleton filling it at 100% height.
                  `aspectRatio` on the Skeleton itself is the form that collapsed the product page's
                  gallery to a hairline, and it passes every numeric check because the other column
                  drives the row height — only a screenshot catches it.

                  Border radius 12px and the grey[100] edge are `Slideshow`'s own, so the square
                  lands exactly where the swiper will.
                */}
                <Box sx={{ width: '100%', aspectRatio: '1 / 1' }}>
                  <Skeleton
                    variant="rounded"
                    sx={{ width: '100%', height: '100%', borderRadius: '12px' }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Stack spacing={4}>
                  {/* The real InfoBlock/InfoRow/InfoItem, `value={undefined}` — InfoItem already
                      draws that as a Skeleton, so these rows' heights are correct by construction
                      and stay correct if a field is added. The trailing empty InfoItem in the last
                      row mirrors the real page's spacer. */}
                  <InfoBlock>
                    <InfoRow>
                      <InfoItem label="Certificate ID" value={undefined} />
                      <InfoItem label="Creation date" value={undefined} />
                    </InfoRow>
                    <Divider sx={{ pr: 4, pl: 4 }} />
                    <InfoRow>
                      <InfoItem label="Product" value={undefined} />
                      <InfoItem label="Manufacture date" value={undefined} />
                    </InfoRow>
                    <Divider sx={{ pr: 4, pl: 4 }} />
                    <InfoRow>
                      <InfoItem label="Tag" value={undefined} />
                      <InfoItem label="" value={''} />
                    </InfoRow>
                    <Divider sx={{ pr: 4, pl: 4 }} />
                    {/* VRG-528 — the Activation row, matching the real page's fourth
                        row. Without it the block grows on load and shifts both
                        accordions down (§19 footprint parity). */}
                    <InfoRow>
                      <InfoItem label="Activation" value={undefined} />
                      <InfoItem label="" value={<></>} />
                    </InfoRow>
                  </InfoBlock>

                  <AccordionSkeleton expanded />
                  <AccordionSkeleton />
                </Stack>
              </Grid>
            </Grid>
          </TabPanel>
        </Tabs>
      </Box>
    </>
  )
}
